import { AnyJson, JsonObj, ReadonlyDeep } from '../types';

export function getErrorCode(err: unknown): unknown {
	if (typeof err === 'object' && err !== null && 'code' in err) {
		return err.code;
	}
}

export function getErrorMsg(err: unknown): string | undefined {
	if (
		typeof err === 'object' &&
		err !== null &&
		'message' in err &&
		typeof err.message === 'string'
	) {
		return err.message;
	}
}

export function isDef<T>(thing: T | undefined): thing is T {
	return typeof thing !== 'undefined';
}

export function notNull<T>(thing: T | null): thing is T {
	return thing !== null;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function isObj(thing: unknown): thing is Object {
	return typeof thing === 'object' && thing !== null;
}

export function isNum(thing: unknown): thing is number {
	return typeof thing === 'number';
}

export function isPosInt(thing: unknown): thing is number {
	return typeof thing === 'number' && Number.isSafeInteger(thing) && thing > 0;
}

export function isNonNegInt(thing: unknown): thing is number {
	return typeof thing === 'number' && Number.isSafeInteger(thing) && thing >= 0;
}

export function isBigInt(thing: unknown): thing is bigint {
	return typeof thing === 'bigint';
}

export function isStr(thing: unknown): thing is string {
	return typeof thing === 'string';
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function isFunc(thing: unknown): thing is Function {
	return typeof thing === 'function';
}

export function isBool(thing: unknown): thing is boolean {
	return thing === true || thing === false;
}

export const isDate = (thing: unknown): thing is Date => thing instanceof Date;

export function isBuf(thing: unknown): thing is Buffer {
	return Buffer.isBuffer(thing);
}

export function isAry(thing: unknown): thing is Array<any> {
	return Array.isArray(thing);
}

export function isNumAry(thing: unknown): thing is Array<number> {
	return Array.isArray(thing) && thing.length > 0 && isNum(thing[0]);
}

export function isAry2(thing: unknown): thing is ReadonlyArray<any> {
	return Array.isArray(thing);
}

export function isJsonObj(thing: AnyJson | undefined): thing is JsonObj {
	if (
		!isDef(thing) ||
		isAry(thing) ||
		isBool(thing) ||
		isNum(thing) ||
		isStr(thing) ||
		thing === null
	) {
		return false;
	}
	return true;
}

export function isIntStr(thing: unknown): thing is string {
	return isStr(thing) && /^0|-?[1-9]\d*$/.test(thing);
}

export function getIntStr(thing: unknown): number | undefined;
export function getIntStr<T>(thing: unknown, def: T): number | T;
export function getIntStr<T>(thing: unknown, def?: T): number | T | undefined {
	return isIntStr(thing) ? Number.parseInt(thing, 10) : def;
}

export function isNonNegIntStr(thing: unknown): thing is string {
	return isStr(thing) && /^0|[1-9]\d*$/.test(thing);
}

export function getNonNegIntStr(thing: unknown): number | undefined;
export function getNonNegIntStr<T>(thing: unknown, def: T): number | T;
export function getNonNegIntStr<T>(thing: unknown, def?: T): number | T | undefined {
	return isNonNegIntStr(thing) ? Number.parseInt(thing, 10) : def;
}

export function isPosIntStr(thing: unknown): thing is string {
	return isStr(thing) && /^[1-9]\d*$/.test(thing);
}

export function getPosIntStr(thing: unknown): number | undefined;
export function getPosIntStr<T>(thing: unknown, def: T): number | T;
export function getPosIntStr<T>(thing: unknown, def?: T): number | T | undefined {
	return isPosIntStr(thing) ? Number.parseInt(thing, 10) : def;
}

export function setProperties<T extends Record<string, unknown>>(a: T, b: Partial<T>): T {
	for (const key of Object.keys(a)) {
		const val = b[key as keyof T];
		if (isDef(val)) {
			a[key as keyof T] = val as any;
		}
	}

	return a;
}

export function hasFlag(input: unknown, flag: number): boolean {
	return isNum(input) && (input & flag) >>> 0 === flag;
}

// Given a string, escapes it so that it can be used as a string literal in a JS RegExp
// See:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
// https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
export function escapeRegExp(str: string): string {
	return str.replace(/[.*+?^${}()|[\]/\\]/g, '\\$&');
}

// Fastest
// https://jsperf.com/flatten-an-array-loop-vs-reduce/6
export const flatten = <T>(input: readonly (readonly T[])[]): T[] => {
	const flattened: T[] = [];
	const inputLength = input.length;
	for (let i = 0; i < inputLength; ++i) {
		const current = input[i];
		const currentLength = current.length;
		for (let j = 0; j < currentLength; ++j) {
			flattened.push(current[j]);
		}
	}

	return flattened;
};

// From https://stackoverflow.com/questions/26156292/trim-specific-character-from-a-string
export function trimAny(str: string, chars: string): string;
export function trimAny(str: string | undefined, chars: string): string | undefined;
export function trimAny(str: string | undefined, chars: string): string | undefined {
	if (!isDef(str)) {
		return;
	}

	let start = 0,
		end = str.length;

	while (start < end && chars.indexOf(str[start]) >= 0) {
		++start;
	}

	while (end > start && chars.indexOf(str[end - 1]) >= 0) {
		--end;
	}

	return start > 0 || end < str.length ? str.substring(start, end) : str;
}

export function trimStartEnd(str: string, toTrim: string): string {
	const len = toTrim.length;
	if (str.length >= 2 * len) {
		// Remove surrounding quote marks from name part
		// 'Ross Johnson' => Ross Johnson
		if (str.startsWith(toTrim) && str.endsWith(toTrim)) {
			str = str.substr(len, str.length - 2 * len);
		}
	}

	return str;
}

export function trimQuotePairNoWs(str: string): string {
	if (str.length >= 2) {
		// Remove surrounding quote marks from name part
		// 'Ross Johnson' => Ross Johnson
		if (
			(str.startsWith("'") && str.endsWith("'")) ||
			(str.startsWith('"') && str.endsWith('"'))
		) {
			str = str.substr(1, str.length - 2);
		}
	}

	return str;
}

export function trimQuotePair(str: string): string {
	return trimQuotePairNoWs(str.trim());
}
export const trimQuotes = trimQuotePair;

export function partition<T, U>(
	a: Iterable<T | U>,
	callbackfn: (value: T | U) => value is T
): [T[], U[]];
export function partition<T>(a: Iterable<T>, callbackfn: (value: T) => boolean): [T[], T[]];
export function partition<T>(a: Iterable<T>, callbackfn: (value: T) => boolean): [T[], T[]] {
	const yes: T[] = [];
	const no: T[] = [];

	for (const item of a) {
		if (callbackfn(item)) {
			yes.push(item);
		} else {
			no.push(item);
		}
	}

	return [yes, no];
}

// RDJ: I can never remember the name of this function, so aliasing :p
export { partition as segregate };
export { partition as bifurcate };
export { partition as separate };

export function areArraysEqual(...arrays: (readonly any[])[]): boolean {
	// If comparing less than two arrays, automatically equal
	if (arrays.length <= 1) {
		return true;
	}

	// Do basic length check
	const length = arrays[0].length;
	for (let idxArray = 1; idxArray < arrays.length; ++idxArray) {
		if (arrays[idxArray].length !== length) {
			return false;
		}
	}

	// For each index in the arrays, ensure each value is the same
	for (let idxItem = 0; idxItem < length; ++idxItem) {
		const value = arrays[0][idxItem];
		for (let idxArray = 1; idxArray < arrays.length; ++idxArray) {
			if (arrays[idxArray][idxItem] !== value) {
				return false;
			}
		}
	}

	return true;
}

export function replaceControlsAndForwardSlash(str: string): string {
	// Remove control characters and "/"
	return str.replace(/[\u0000-\u001F\u007F-\u009F/]/g, '_');
}

export function replacePrivateUseChars(str: string, replacement = '?'): string {
	// /[\u{E000}-\u{F8FF}\u{F0000}-\u{FFFFF}\u{100000}-\u{10FFFF}]/u
	// =>
	// /(?:[\uE000-\uF8FF]|[\uDB80-\uDBFF][\uDC00-\uDFFF])/
	// Using https://mothereff.in/regexpu
	return str.replace(/(?:[\uE000-\uF8FF]|[\uDB80-\uDBFF][\uDC00-\uDFFF])/g, replacement);
}

export function getTypedObjectKeys<T extends Record<string, unknown>>(o: T): (keyof T)[] {
	return Object.keys(o) as (keyof T)[];
}

export function getEnumKey(
	map: { [key: string]: string | number },
	value: number | string,
	def?: string
): string;
export function getEnumKey(
	map: { [key: string]: string | number },
	value: number | string | undefined,
	def?: string
): string;
export function getEnumKey(
	map: { [key: string]: string | number },
	value: number | string | undefined,
	def = ''
): string {
	if (!isDef(value)) {
		return '<undefined>';
	}

	for (const key in map) {
		const val = map[key];
		if (val === value) {
			return key;
		}
	}

	return def;
}

export function* numRangeInclusiveGenerator(
	xInclusive: number,
	yInclusive: number,
	step = 1
): Iterable<number> {
	while (xInclusive <= yInclusive) {
		yield xInclusive;
		xInclusive += step;
	}
}

export function intRangeInclusive(x: number, y: number): number[] {
	return [...numRangeInclusiveGenerator(x, y)];
}

export function intRangeZeroUpTo(yExclusive: number): number[] {
	return [...numRangeInclusiveGenerator(0, yExclusive - 1)];
}

export function makeReadonlyDeep<T>(x: T): ReadonlyDeep<T> {
	return x;
}

// Remove control characters and "/"
export function cleanPosixBasename(name: string): string {
	return name.replace(/[\u0000-\u001F\u007F-\u009F/]/g, '_').trim();
}

export const unleak = (s: string): string => (' ' + s).slice(1);

// Intentionally returns void, to make the array mutation of .sort more obvious
export const sortLowFirst = (ns: number[]): void => {
	ns.sort((a, b) => a - b);
};

export const uniqAndSortLowFirst = (ns: Iterable<number>): number[] => {
	const arr = [...new Set(ns)];
	sortLowFirst(arr);
	return arr;
};

// Intentionally returns void, to make the array mutation of .sort more obvious
export const sortHighFirst = (ns: number[]): void => {
	ns.sort((a, b) => b - a);
};

export function mod(n: number, m: number): number {
	return ((n % m) + m) % m;
}

export function isArraySorted(arr: number[]): boolean {
	const len = arr.length;
	for (let i = 1; i < len; i++) {
		if (arr[i] < arr[i - 1]) {
			return false;
		}
	}
	return true;
}

export function padOutToMultiple(len: number, mult: number): number {
	if (!isNonNegInt(len)) {
		throw new Error('Expected length to be a non-negative integer');
	}

	if (!isPosInt(mult)) {
		throw new Error('Expected mult to be a positive integer');
	}
	if (mult === 1) {
		return len;
	}

	const remainder = len % mult;
	const pad = remainder === 0 ? 0 : mult - remainder;
	return len + pad;
}

export function padOutToPow2Multiple(
	len: number,
	mult: 1 | 2 | 4 | 8 | 16 | 32 | 64 | 128 | 256 | 512 | 1024 | 2048 | 4096 | 8192
): number {
	if (!isNonNegInt(len)) {
		throw new Error('Expected length to be a non-negative integer');
	}
	if (!isPosInt(mult)) {
		throw new Error('Expected mult to be a positive integer');
	}
	if (mult === 1) {
		return len;
	}

	return (len + mult - 1) & -mult;
}

export function hasUndefinedProps<T>(obj: T): boolean {
	for (const prop in obj) {
		if (typeof obj[prop] === 'undefined') {
			return true;
		}
	}

	return false;
}

export function removeUndefinedProps<T>(obj: T): Partial<T> {
	for (const prop in obj) {
		if (typeof obj[prop] === 'undefined') {
			delete obj[prop];
		}
	}

	return obj;
}
